import type { ClientToServerMessage, ServerToClientMessage } from "@mmotdg/shared";

type MessageListener = (msg: ServerToClientMessage) => void;
type CloseListener = (code: number) => void;

export class GameSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<MessageListener>();
  private closeListeners = new Set<CloseListener>();

  connect(host: string, port: number, token: string, roomId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${proto}://${host}:${port}/ws?token=${encodeURIComponent(token)}&room=${encodeURIComponent(roomId)}`;
      const ws = new WebSocket(url);
      this.ws = ws;

      const onOpen = () => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onError);
        resolve();
      };
      const onError = (err: Event) => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onError);
        reject(err);
      };
      ws.addEventListener("open", onOpen);
      ws.addEventListener("error", onError);

      ws.addEventListener("message", (event) => {
        try {
          const msg = JSON.parse(event.data) as ServerToClientMessage;
          for (const l of this.listeners) l(msg);
        } catch {
          // ignore
        }
      });

      ws.addEventListener("close", (event) => {
        for (const l of this.closeListeners) l(event.code);
        this.ws = null;
      });
    });
  }

  send(msg: ClientToServerMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  onMessage(listener: MessageListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onClose(listener: CloseListener): () => void {
    this.closeListeners.add(listener);
    return () => this.closeListeners.delete(listener);
  }

  disconnect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
    this.ws = null;
  }
}

export const gameSocket = new GameSocket();
